import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import instructChar from '../images/instruct-o.webp';
import typingSound from '../sounds/escribir.mp3';

const SlideComponent = ({ actual, first, second, third, fourth, setCanGoNext }) => {
    const { t, i18n } = useTranslation();
    const displayOption = (item) => i18n.language === 'en' && item.option_en ? item.option_en : item.option;
    const [selectedButtonGroup1, setSelectedButtonGroup1] = useState(null);
    const [selectedButtonsGroup2, setSelectedButtonsGroup2] = useState({});
    const [title, setTitle] = useState(null);
    const [displayedText, setDisplayedText] = useState('');
    const [typingActive, setTypingActive] = useState(false);

    const overlayRef = useRef(null);
    const audioRef = useRef(null);

    useEffect(() => {
        const savedButtonGroup1 = localStorage.getItem('selected2');
        const savedButtonsGroup2 = {
            1: JSON.parse(localStorage.getItem('selected3') || '[]'),
            2: JSON.parse(localStorage.getItem('selected4') || '[]'),
            3: JSON.parse(localStorage.getItem('selected5') || '[]'),
        };
        const savedTest = JSON.parse(localStorage.getItem('currentTest') || '[]');
        setTitle(savedTest.title);
        if (savedButtonGroup1) setSelectedButtonGroup1(savedButtonGroup1);
        setSelectedButtonsGroup2(savedButtonsGroup2);
    }, []);

    useEffect(() => {
        const selectedCount = Object.values(selectedButtonsGroup2).reduce((total, current, index) => {
            if (index + 1 === 2) {
                return total + (current.includes(third.question[0].option) ? 2 : current.length);
            }
            return total + current.length;
        }, 0);
        if (setCanGoNext) setCanGoNext(selectedCount === 6 && selectedButtonGroup1 !== null);
    }, [selectedButtonGroup1, selectedButtonsGroup2, third, setCanGoNext]);

    useEffect(() => {
        const overlay = overlayRef.current;
        if (!overlay) return;
        const observer = new MutationObserver(() => {
            if (overlay.style.display !== 'none') {
                setDisplayedText('');
                setTypingActive(true);
            }
        });
        observer.observe(overlay, { attributes: true, attributeFilter: ['style'] });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!typingActive) return;
        const fullText = t('olfactory.bubble');
        if (displayedText.length >= fullText.length) {
            setTypingActive(false);
            if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
            return;
        }
        if (audioRef.current && audioRef.current.paused) {
            audioRef.current.volume = 0.3;
            audioRef.current.loop = true;
            audioRef.current.play().catch(() => {});
        }
        const timeout = setTimeout(() => {
            setDisplayedText(fullText.slice(0, displayedText.length + 1));
        }, 28);
        return () => clearTimeout(timeout);
    }, [typingActive, displayedText, t]);

    const handleButtonClickGroup1 = (buttonId) => {
        setSelectedButtonGroup1(buttonId === selectedButtonGroup1 ? null : buttonId);
        localStorage.setItem('selected2', buttonId === selectedButtonGroup1 ? '' : buttonId);
    };

    const handleButtonClickGroup2 = (column, buttonId) => {
        const currentSelectedButtons = selectedButtonsGroup2[column] || [];
        const index = currentSelectedButtons.indexOf(buttonId);
        const isSpecialButton = column === 2 && buttonId === third.question[0].option;

        if (isSpecialButton) {
            if (index !== -1) {
                currentSelectedButtons.splice(index, 1);
            } else if (currentSelectedButtons.length === 0) {
                currentSelectedButtons.push(buttonId);
            }
        } else {
            if (index === -1 && currentSelectedButtons.length < (column === 1 ? 3 : column === 2 ? 2 : 1)) {
                if (column !== 2 || !currentSelectedButtons.includes(third.question[0].option)) {
                    currentSelectedButtons.push(buttonId);
                }
            } else if (index !== -1) {
                currentSelectedButtons.splice(index, 1);
            }
        }

        setSelectedButtonsGroup2({ ...selectedButtonsGroup2, [column]: currentSelectedButtons });
        localStorage.setItem(`selected${column + 2}`, JSON.stringify(currentSelectedButtons));
    };

    const isButtonSelectedGroup2 = (column, buttonId) => {
        return selectedButtonsGroup2[column]?.includes(buttonId);
    };

    const handleQuitInstructions = () => {
        const instructionsDiv = overlayRef.current;
        if (instructionsDiv) instructionsDiv.style.display = 'none';
        setTypingActive(false);
        if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    };

    return (
        <div className="slide-container">
            <audio ref={audioRef} src={typingSound} />
            <div className="overlay" id="instructions-div" ref={overlayRef}>
                <div className="vi-instruct-scene">
                    <img src={instructChar} alt="" className="vi-instruct-char" />
                    <div className="vi-instruct-bubble-wrap">
                        <div className="vi-bubble-row">
                            <svg className="vi-tail-svg" width="28" height="20" viewBox="0 0 28 20">
                                <polygon points="28,0 28,20 0,10" fill="#fff" stroke="#111" strokeWidth="3" strokeLinejoin="round"/>
                            </svg>
                            <div className="vi-bubble-shell">
                                <div className="vi-instruct-bubble">
                                    <p className="vi-instruct-text">{displayedText}<span className="vi-instruct-cursor">|</span></p>
                                </div>
                                <button className="vi-instruct-close" onClick={handleQuitInstructions} aria-label={t('common.close')}>×</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="ts-phase-icon" role="img" aria-label={t('stats.olfactory_phase')}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4 C11 4 9 10 9 14 C9 17 7 18 7 20"/>
                    <path d="M13 4 C13 4 15 10 15 14 C15 17 17 18 17 20"/>
                    <path d="M7 20 Q12 23 17 20"/>
                </svg>
                <span className="ts-phase-icon-tip">{t('stats.olfactory_phase')}</span>
            </div>
            <div className="actual-wine">{title}</div>

            <div className="ol-phase">
                <div className="ol-title"><span>{t('olfactory.title')}</span></div>

                <div className="ol-group" role="group">
                    <div className="ol-intensity">
                        <div className="ol-section-label">{t('olfactory.intensity')}</div>
                        <div className="ol-btn-row">
                            {first.question.map((item, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className={`ol-btn${selectedButtonGroup1 === item.option ? ' ol-selected' : ''}`}
                                    onClick={() => handleButtonClickGroup1(item.option)}
                                >
                                    {displayOption(item)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="ol-divider" />

                    <div className="ol-aromas">
                        <div className="ol-aroma-col">
                            <div className="ol-section-label">{t('olfactory.primary')}</div>
                            <div className="ol-btn-stack">
                                {second.question.map((item, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className={`ol-btn${isButtonSelectedGroup2(1, item.option) ? ' ol-selected' : ''}`}
                                        onClick={() => handleButtonClickGroup2(1, item.option)}
                                    >
                                        {displayOption(item)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="ol-col-divider" />

                        <div className="ol-aroma-col">
                            <div className="ol-section-label">{t('olfactory.secondary')}</div>
                            <div className="ol-btn-stack">
                                {third.question.map((item, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className={`ol-btn${isButtonSelectedGroup2(2, item.option) ? ' ol-selected' : ''}`}
                                        onClick={() => handleButtonClickGroup2(2, item.option)}
                                        id={item.option}
                                    >
                                        {displayOption(item)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="ol-col-divider" />

                        <div className="ol-aroma-col">
                            <div className="ol-section-label">{t('olfactory.tertiary')}</div>
                            <div className="ol-btn-stack">
                                {fourth.question.map((item, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className={`ol-btn${isButtonSelectedGroup2(3, item.option) ? ' ol-selected' : ''}`}
                                        onClick={() => handleButtonClickGroup2(3, item.option)}
                                    >
                                        {displayOption(item)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SlideComponent;
